import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import SchoolDashboardPreview from '../components/school/SchoolDashboardPreview';
import IntegrationDiagram from '../components/school/IntegrationDiagram';
import SecurityComplianceSection from '../components/school/SecurityComplianceSection';
import {
  BuildingOffice2Icon,
  ChartBarSquareIcon,
  ArrowsRightLeftIcon,
  ShieldCheckIcon,
  KeyIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ServerStackIcon,
  DocumentCheckIcon,
  LockClosedIcon,
  CloudArrowUpIcon,
  UserGroupIcon,
  ChartPieIcon,
  BellAlertIcon,
  ClipboardDocumentCheckIcon,
  FingerPrintIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const ForSchools: React.FC = () => {

  const benefits = [
    {
      icon: ChartBarSquareIcon,
      title: 'School-Wide Analytics',
      description: 'Compare class performance, identify trends across year levels, and measure progress against state benchmarks in real-time.',
    },
    {
      icon: ArrowsRightLeftIcon,
      title: 'Seamless Integration',
      description: 'Connect with your existing SIS, LMS, and administration systems through our comprehensive API suite.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Enterprise Security',
      description: 'Building towards NSW Government cyber security compliance and ISO 27001 standards, with local data sovereignty and mandatory breach notification.',
    },
    {
      icon: KeyIcon,
      title: 'Single Sign-On',
      description: 'Integrate with Microsoft, Google Workspace, or your existing identity provider for frictionless access.',
    },
    {
      icon: GlobeAltIcon,
      title: 'Benchmark Comparisons',
      description: 'See how your school performs against aggregated anonymised data from other schools in your region.',
    },
    {
      icon: BellAlertIcon,
      title: 'Early Warning System',
      description: 'Automated alerts for students at risk of falling behind, enabling timely intervention across all classes.',
    },
  ];

  const classComparison = {
    title: 'Class & Cohort Comparison',
    description: 'Gain unprecedented visibility into how classes and year levels compare across key metrics.',
    features: [
      {
        title: 'Class Performance Dashboard',
        items: [
          'Side-by-side class mastery comparisons',
          'Subject-by-subject breakdowns',
          'Engagement and completion metrics',
          'Progress velocity tracking',
        ],
      },
      {
        title: 'Cohort Analytics',
        items: [
          'Year-over-year progression analysis',
          'Identify high-performing teaching approaches',
          'Resource allocation insights',
          'Curriculum coverage tracking',
        ],
      },
      {
        title: 'Intervention Triggers',
        items: [
          'Automated underperformance alerts',
          'Class-level support recommendations',
          'Teacher notification workflows',
          'Parent communication templates',
        ],
      },
    ],
  };

  const integrations = [
    {
      category: 'Student Information Systems',
      description: 'Seamless bidirectional sync with your existing student management platform',
      systems: [
        { name: 'Sentral', description: 'Full API integration with student, class, and attendance data' },
        { name: 'Compass', description: 'Real-time sync of student records and learning activities' },
        { name: 'TASS', description: 'Certified integration partner with automated data exchange' },
        { name: 'Edumate', description: 'Complete student lifecycle management integration' },
        { name: 'Synergetic', description: 'Enterprise-grade sync for independent schools' },
        { name: 'PCSchool', description: 'Comprehensive data mapping and sync capabilities' },
      ],
    },
    {
      category: 'Learning Management Systems',
      description: 'Extend your existing LMS with advanced adaptive learning capabilities',
      systems: [
        { name: 'Schoolbox', description: 'Native integration for content and progress sharing' },
        { name: 'Canvas', description: 'LTI 1.3 compliant with grade passback' },
        { name: 'Moodle', description: 'Plugin-based integration with full data sync' },
        { name: 'Google Classroom', description: 'Assignment sync and roster management' },
        { name: 'Microsoft Teams', description: 'Deep integration with Teams for Education' },
      ],
    },
    {
      category: 'Identity Providers',
      description: 'Single Sign-On support for all major education identity solutions',
      systems: [
        { name: 'Microsoft Entra ID', description: 'Azure AD SSO with SAML 2.0 and OIDC' },
        { name: 'Google Workspace', description: 'Google SSO for seamless student access' },
        { name: 'Okta', description: 'Enterprise identity management integration' },
        { name: 'Active Directory', description: 'On-premises AD federation support' },
        { name: 'SAML 2.0 Compatible', description: 'Any SAML 2.0 compliant identity provider' },
      ],
    },
  ];

  const securityFeatures = [
    {
      icon: LockClosedIcon,
      title: 'NSW Cyber Security Policy Alignment',
      description: 'Building towards full alignment with NSW Government cyber security directives and the Australian Cyber Security Centre Essential Eight.',
    },
    {
      icon: DocumentCheckIcon,
      title: 'ISO 27001 Pathway',
      description: 'Working towards Information Security Management System certification to international standards.',
    },
    {
      icon: FingerPrintIcon,
      title: 'Privacy Act Compliance',
      description: 'Designed for compliance with the Privacy and Personal Information Protection Act 1998 (NSW) and Privacy Act 1988 (Cth).',
    },
    {
      icon: ServerStackIcon,
      title: 'Australian Data Sovereignty',
      description: 'All student data stored exclusively in Australian data centres with no offshore access.',
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Breach Notification Ready',
      description: 'Systems designed for detecting and reporting data breaches as required by NSW legislation.',
    },
    {
      icon: EyeIcon,
      title: 'Audit Trail & Logging',
      description: 'Comprehensive activity logging for compliance reporting and security monitoring.',
    },
  ];

  const complianceStandards = [
    {
      title: 'NSW Department of Education',
      items: [
        'NSW Cyber Security Policy 2024 alignment',
        'DCS-2025-01 Restricted Applications compliance',
        'Technology in Schools procedures adherence',
        'NESA data handling requirements',
      ],
    },
    {
      title: 'Privacy & Data Protection',
      items: [
        'Privacy and Personal Information Protection Act 1998 (NSW)',
        'Health Records and Information Privacy Act 2002',
        'Privacy Act 1988 (Cth) for independent schools',
        'Australian Privacy Principles (APPs)',
      ],
    },
    {
      title: 'Security Standards',
      items: [
        'ISO 27001 Information Security Management',
        'ACSC Essential Eight maturity model',
        'NIST Cybersecurity Framework alignment',
        'SOC 2 Type II attestation',
      ],
    },
    {
      title: 'Accessibility',
      items: [
        'WCAG 2.1 Level AA compliance',
        'AS EN 301 549 ICT accessibility standard',
        'Disability Standards for Education 2005',
        'Screen reader and keyboard navigation support',
      ],
    },
  ];

  const implementationSteps = [
    {
      number: 1,
      title: 'Sign Up & API Access',
      description: 'Create your school account and receive API credentials to connect your existing systems.',
    },
    {
      number: 2,
      title: 'Connect Your Systems',
      description: 'Use our comprehensive API documentation to integrate your SIS, LMS, and identity provider at your own pace.',
    },
    {
      number: 3,
      title: 'Import Your Data',
      description: 'Securely import student records, class structures, and curriculum mappings through our self-service tools.',
    },
    {
      number: 4,
      title: 'Go Live',
      description: 'Start using Lumen8 with your students. Roll out to individual classes or school-wide — your choice.',
    },
  ];

  const schoolTypes = [
    {
      type: 'Government Schools',
      features: [
        'Designing for NSW DET requirements',
        'Building LMBR and SAP integration support',
        'Aligning with DoE Cyber Security Policy',
        'Planning SCOUT reporting compatibility',
      ],
    },
    {
      type: 'Catholic Schools',
      features: [
        'Architecting for diocese-level administration',
        'Building towards CEDP and CEO system support',
        'Designing multi-school networking features',
        'Centralised reporting capabilities planned',
      ],
    },
    {
      type: 'Independent Schools',
      features: [
        'Building with AISNSW requirements in mind',
        'School branding customisation options',
        'Flexible self-service deployment',
        'Direct support channels available',
      ],
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
                Transform Your <span className="text-gradient">Entire School</span>
              </h1>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                Enterprise-grade learning analytics, seamless integration with your existing systems,
                and security that exceeds NSW Government requirements.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Compare class performance across your whole school</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Integrate with Sentral, Compass, TASS, and more</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Building towards NSW Cyber Security Policy & ISO 27001</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                  Request School Demo
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                  See Platform Overview
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="card-premium p-8">
                <div className="text-center mb-6">
                  <BuildingOffice2Icon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">School Admin Dashboard</h3>
                  <p className="text-[var(--color-light-gray)]">Complete visibility across all classes</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">Total Students</span>
                    <span className="text-[var(--color-electric-blue)] font-bold">847</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">Active Classes</span>
                    <span className="text-[var(--color-emerald)] font-bold">32</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-white">School Avg. Mastery</span>
                    <span className="text-[var(--color-violet)] font-bold">76%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[var(--color-amber)]/20 to-[var(--color-amber)]/10 rounded-lg border-l-4 border-[var(--color-amber)]">
                    <span className="text-white">Intervention Alerts</span>
                    <span className="text-[var(--color-amber)] font-bold">12 students</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Why Schools Choose <span className="text-gradient">Lumen8</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Purpose-built for Australian schools with the security, integrations, and insights administrators need
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow">
                  <benefit.icon className="w-12 h-12 text-[var(--color-electric-blue)] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                  <p className="text-[var(--color-light-gray)]">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Class Comparison Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black text-white mb-6">
                {classComparison.title.split(' ').slice(0, -1).join(' ')} <span className="text-gradient">{classComparison.title.split(' ').slice(-1)}</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                {classComparison.description}
              </p>
              <div className="space-y-6">
                {classComparison.features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="glass-card p-6"
                  >
                    <h3 className="text-lg font-bold text-white mb-3">{feature.title}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {feature.items.map((item) => (
                        <div key={item} className="flex items-start gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-[var(--color-light-gray)]">{item}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <SchoolDashboardPreview />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-violet)] rounded-full filter blur-3xl opacity-20"></div>
            </div>
          </div>
        </Container>
      </section>

      {/* School Benchmarking */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Know Where You <span className="text-gradient">Stand</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Compare your school's performance against aggregated, anonymised benchmarks from schools in your region and sector
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full text-center hover-glow">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-6">
                  <ChartPieIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Regional Benchmarks</h3>
                <p className="text-[var(--color-light-gray)]">
                  See how your school compares to others in your local area across key mastery metrics and engagement levels.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full text-center hover-glow">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] p-4 mx-auto mb-6">
                  <UserGroupIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Sector Comparisons</h3>
                <p className="text-[var(--color-light-gray)]">
                  Government, Catholic, and Independent school segments allow for meaningful like-for-like performance analysis.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full text-center hover-glow">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-6">
                  <AcademicCapIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Privacy Protected</h3>
                <p className="text-[var(--color-light-gray)]">
                  All benchmark data is fully anonymised and aggregated. Individual student data never leaves your school.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="mt-12 max-w-4xl mx-auto">
            <div className="card-premium p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Sample School Benchmark Report</h3>
                <p className="text-[var(--color-light-gray)]">Year 5 Mathematics Mastery - Term 3 2024</p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-gradient-to-br from-[var(--color-electric-blue)]/20 to-[var(--color-electric-blue)]/5 rounded-lg border border-[var(--color-electric-blue)]/30">
                  <div className="text-4xl font-black text-[var(--color-electric-blue)] mb-2">76%</div>
                  <div className="text-white font-semibold mb-1">Your School</div>
                  <div className="text-sm text-[var(--color-medium-gray)]">Current average mastery</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <div className="text-4xl font-black text-[var(--color-light-gray)] mb-2">71%</div>
                  <div className="text-white font-semibold mb-1">Regional Average</div>
                  <div className="text-sm text-[var(--color-medium-gray)]">Sydney Metro schools</div>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <div className="text-4xl font-black text-[var(--color-light-gray)] mb-2">73%</div>
                  <div className="text-white font-semibold mb-1">State Average</div>
                  <div className="text-sm text-[var(--color-medium-gray)]">All NSW schools</div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-[var(--color-emerald)]/10 to-[var(--color-emerald)]/5 rounded-lg border-l-4 border-[var(--color-emerald)]">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Above Regional Average</span>
                    <p className="text-sm text-[var(--color-light-gray)] mt-1">
                      Your school is performing 5 percentage points above the regional average in Year 5 Mathematics.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Seamless <span className="text-gradient">Integration</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Connect with your existing Student Information Systems, Learning Management Systems, and Identity Providers
            </p>
          </div>

          <IntegrationDiagram />

          <div className="mt-16 space-y-8">
            {integrations.map((category, categoryIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  {category.category === 'Student Information Systems' && (
                    <ServerStackIcon className="w-10 h-10 text-[var(--color-electric-blue)]" />
                  )}
                  {category.category === 'Learning Management Systems' && (
                    <AcademicCapIcon className="w-10 h-10 text-[var(--color-violet)]" />
                  )}
                  {category.category === 'Identity Providers' && (
                    <KeyIcon className="w-10 h-10 text-[var(--color-emerald)]" />
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-white">{category.category}</h3>
                    <p className="text-[var(--color-light-gray)]">{category.description}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.systems.map((system) => (
                    <div
                      key={system.name}
                      className="p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <h4 className="text-white font-semibold mb-1">{system.name}</h4>
                      <p className="text-sm text-[var(--color-medium-gray)]">{system.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-[var(--color-light-gray)] mb-4">
              Don't see your system? We offer custom integration development for enterprise clients.
            </p>
            <Link to="/contact" className="btn-glass inline-flex items-center gap-2">
              Discuss Custom Integration
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </Container>
      </section>

      {/* Single Sign-On Section */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black text-white mb-6">
                Single Sign-On for <span className="text-gradient">Everyone</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                Students, teachers, and parents access Lumen8 with their existing school credentials.
                No new passwords to remember, no friction to adoption.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-electric-blue)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-electric-blue)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Microsoft Entra ID (Azure AD)</h3>
                    <p className="text-[var(--color-light-gray)]">SAML 2.0 and OpenID Connect support with automatic user provisioning via SCIM.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-violet)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-violet)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Google Workspace for Education</h3>
                    <p className="text-[var(--color-light-gray)]">Native Google SSO integration with Classroom roster sync and assignment management.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-emerald)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Active Directory Federation</h3>
                    <p className="text-[var(--color-light-gray)]">On-premises AD FS integration for schools with existing directory infrastructure.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-amber)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-amber)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Any SAML 2.0 Provider</h3>
                    <p className="text-[var(--color-light-gray)]">Support for Okta, OneLogin, and any SAML 2.0 compliant identity provider your school uses.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="card-premium p-8 text-center">
                <KeyIcon className="w-20 h-20 text-[var(--color-electric-blue)] mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">One Click Access</h3>
                <p className="text-[var(--color-light-gray)] mb-6">
                  Students click the Lumen8 icon in their school portal and they're in — no additional login required.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-[var(--color-electric-blue)]">0</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">New passwords</div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <div className="text-2xl font-bold text-[var(--color-emerald)]">100%</div>
                    <div className="text-sm text-[var(--color-medium-gray)]">Adoption ready</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Security & Compliance Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Security That Exceeds <span className="text-gradient">Requirements</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Built to meet and exceed NSW Government cyber security standards with enterprise-grade protection for student data
            </p>
          </div>

          <SecurityComplianceSection />

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-6 h-full hover-glow">
                  <feature.icon className="w-10 h-10 text-[var(--color-electric-blue)] mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Compliance Standards */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Compliance <span className="text-gradient">Framework</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Comprehensive adherence to Australian education regulations and international security standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {complianceStandards.map((standard, index) => (
              <motion.div
                key={standard.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="glass-card p-8"
              >
                <h3 className="text-2xl font-bold text-white mb-6">{standard.title}</h3>
                <div className="space-y-3">
                  {standard.items.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <ClipboardDocumentCheckIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--color-light-gray)]">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="card-premium p-8 max-w-2xl mx-auto">
              <DocumentCheckIcon className="w-12 h-12 text-[var(--color-electric-blue)] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Request Compliance Documentation</h3>
              <p className="text-[var(--color-light-gray)] mb-6">
                We provide comprehensive compliance documentation packages for procurement and IT security review processes.
              </p>
              <Link to="/contact" className="btn-premium inline-flex items-center gap-2">
                Request Documentation
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* School Types */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Designed for <span className="text-gradient">All School Types</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Building with Government, Catholic, and Independent school requirements in mind
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {schoolTypes.map((school, index) => (
              <motion.div
                key={school.type}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow">
                  <h3 className="text-2xl font-bold text-white mb-6">{school.type}</h3>
                  <div className="space-y-3">
                    {school.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                        <span className="text-[var(--color-light-gray)]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Implementation Timeline */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Self-Service <span className="text-gradient">Integration</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Integrate at your own pace with our comprehensive APIs and documentation. Optional support available when you need it.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[var(--color-electric-blue)] via-[var(--color-violet)] to-[var(--color-emerald)] hidden md:block"></div>

              <div className="space-y-8">
                {implementationSteps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="relative flex gap-6"
                  >
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-violet)] flex items-center justify-center text-white font-bold text-2xl z-10">
                      {step.number}
                    </div>
                    <div className="glass-card p-6 flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-[var(--color-light-gray)]">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <BuildingOffice2Icon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-6" />
            <h2 className="text-4xl font-black text-white mb-6">
              Ready to Transform <span className="text-gradient">Your School?</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              Join the growing network of Australian schools using Lumen8 to deliver real-time insights,
              improve outcomes, and stay ahead of compliance requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                Request School Demo
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                See How It Works
              </Link>
            </div>
            <p className="mt-8 text-sm text-[var(--color-medium-gray)]">
              Full API documentation and optional integration support available
            </p>
          </div>
        </Container>
      </section>
    </>
  );
};

export default ForSchools;
